import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight, Sparkles, Info } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion";
import { DotGrid } from "@/components/visuals";
import { api } from "@/lib/api";
import { COURSES } from "@/data/courses";
import { COUNTRIES, CA_PROVINCES } from "@/data/geography";
import { cartApi } from "@/lib/cart";

const STEP_TITLES = ["Student details", "Courses & pathway", "Review & submit"];

export const TYPE_LABELS = {
  ontario: "Ontario based",
  out_of_province: "Out-of-province (Canada)",
  international: "International Students",
};

export const PER_CREDIT = { ontario: 490, out_of_province: 490, international: 890 };
// Tuition bundled discounts: student_type -> { min credits, rate }
export const BUNDLED_DISCOUNTS = {
  ontario: { min: 3, rate: 0.05442 },
  out_of_province: { min: 6, rate: 0.05102 },
  international: { min: 6, rate: 0.171349 },
};
export const SOR_FEE = 200;
export const PLAR_FEE = 200;
export const SOR_NAME = "EGOS as Full-Time School + Ontario Student Record (OSR) setup";
export const PLAR_NAME = "Prior Learning Assessment and Recognition (PLAR)";

export const PLAR_NOTE =
  "Students need to add the minimum required course credits as per their PLAR from EGOS to be eligible to get the OSSD from EGOS. If fewer credits are added at this point, you'll be asked to add and pay for more before admission can be considered for OSSD, till then these will be considered as only individual credits required from EGOS.";

export const plarApplies = ({ pathway, student_type, grade_level }) =>
  pathway === "ossd" && (student_type !== "ontario" || grade_level === "homeschool");

export const ossdMinCredits = ({ student_type, grade_level }) =>
  student_type !== "ontario" || grade_level === "homeschool" ? 6 : 3;

export function buildFees({ student_type, pathway, courses, plar_completed, grade_level, extra_items = [] }) {
  const fees = [];
  const per = PER_CREDIT[student_type] ?? 490;
  if (courses.length) {
    fees.push({ name: `Tuition — ${courses.length} credit${courses.length === 1 ? "" : "s"} @ $${per}`, amount: per * courses.length });
  }
  for (const it of extra_items) {
    if (it.type === "plar") fees.push({ name: "PLAR assessment", amount: 200 });
    else if (it.type === "extension") fees.push({ name: `Course extension — ${it.months} month${it.months > 1 ? "s" : ""}`, amount: 70 * it.months });
    else if (it.type === "transfer") fees.push({ name: `Course transfer (${it.from_course} → ${it.to_course})`, amount: 150 });
  }
  if (pathway === "ossd") {
    fees.push({ name: SOR_NAME, amount: SOR_FEE });
  }
  if (plarApplies({ pathway, student_type, grade_level }) && plar_completed === "no") {
    fees.push({ name: PLAR_NAME, amount: PLAR_FEE });
  }
  return fees;
}

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export default function Admissions() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(() => {
    const fromCart = params.get("checkout") ? cartApi.get() : [];
    const fromQuery = params.get("course") ? [params.get("course")] : [];
    return {
      student_name: "",
      email: "",
      phone: "",
      school_board: "",
      grade_level: "",
      student_type: "ontario",
      parent_name: "",
      parent_email: "",
      address: "",
      country: "Canada",
      province: "Ontario",
      pathway: "",
      plar_completed: "",
      osr_info: "",
      current_sor_is_egos: "",
      full_time_school_name: "",
      coupon_code: "",
      courses: [...new Set([...fromCart.filter((c) => typeof c === "string"), ...fromQuery])],
      extra_items: fromCart.filter((c) => typeof c === "object"),
      message: "",
      consent_privacy: false,
      consent_refund: false,
    };
  });
  const [submitting, setSubmitting] = useState(false);
  const [refId, setRefId] = useState(null);
  const [couponInput, setCouponInput] = useState("");
  const [couponInfo, setCouponInfo] = useState(null); // {code, discount_type, discount_value}

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setType = (v) =>
    setForm((f) => ({
      ...f,
      student_type: v,
      country: v === "international" ? "" : "Canada",
      province: v === "ontario" ? "Ontario" : "",
      plar_completed: plarApplies({ pathway: f.pathway, student_type: v, grade_level: f.grade_level }) ? f.plar_completed : "",
    }));

  const setGrade = (v) =>
    setForm((f) => ({
      ...f,
      grade_level: v,
      plar_completed: plarApplies({ pathway: f.pathway, student_type: f.student_type, grade_level: v }) ? f.plar_completed : "",
    }));

  const setPathway = (v) =>
    setForm((f) => ({
      ...f,
      pathway: v,
      plar_completed: plarApplies({ pathway: v, student_type: f.student_type, grade_level: f.grade_level }) ? f.plar_completed : "",
      osr_info: v === "individual" ? f.osr_info : "",
      current_sor_is_egos: v === "individual" ? f.current_sor_is_egos : "",
      full_time_school_name: v === "individual" ? f.full_time_school_name : "",
    }));

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    try {
      const res = await api.post("/coupons/validate", { code: couponInput.trim() });
      setCouponInfo(res.data);
      set("coupon_code", res.data.code);
      toast.success(`Partner code ${res.data.code} applied`);
    } catch {
      setCouponInfo(null);
      set("coupon_code", "");
      toast.error("Invalid or expired partner code.");
    }
  };

  const fees = useMemo(() => buildFees(form), [form]);
  const subtotal = fees.reduce((t, f) => t + f.amount, 0);
  const tuitionAmount = fees.find((f) => f.name.startsWith("Tuition"))?.amount ?? 0;
  const bundledRule = BUNDLED_DISCOUNTS[form.student_type];
  const bundled = bundledRule && form.courses.length >= bundledRule.min ? Math.round(tuitionAmount * bundledRule.rate) : 0;
  const discount = couponInfo
    ? couponInfo.discount_type === "percent"
      ? Math.floor(((tuitionAmount - bundled) * couponInfo.discount_value) / 100)
      : Math.min(Math.floor(couponInfo.discount_value), subtotal - bundled)
    : 0;
  const total = subtotal - bundled - discount;
  // EGOS-specific fees (Full-Time School setup + PLAR) render first, then a divider, then tuition/services.
  const dividerIdx = fees.findIndex((f) => f.name === SOR_NAME || f.name === PLAR_NAME);

  const next = () => {
    if (step === 0) {
      if (!form.student_name.trim() || !emailOk(form.email) || !form.grade_level) {
        toast.error("Please add the student name, a valid email and the grade level.");
        return;
      }
      if (!form.phone.trim()) {
        toast.error("Please add a phone number.");
        return;
      }
      if (!form.parent_name.trim() || !emailOk(form.parent_email)) {
        toast.error("Please add the parent/guardian name and a valid email.");
        return;
      }
      if (!form.address.trim() || !form.country) {
        toast.error("Please add your address and country.");
        return;
      }
      if (form.country === "Canada" && !form.province) {
        toast.error("Please choose your province.");
        return;
      }
    }
    if (step === 1) {
      if (!form.pathway && (form.courses.length > 0 || !form.extra_items.length)) {
        toast.error("Choose between Individual credits only and OSSD from EGOS.");
        return;
      }
      const min = form.pathway === "ossd" ? ossdMinCredits(form) : 1;
      if (form.courses.length < min && !(form.extra_items.length > 0 && form.courses.length === 0 && form.pathway !== "ossd")) {
        toast.error(form.pathway === "ossd" ? `OSSD from EGOS requires a minimum of ${min} credits.` : "Select at least one course to continue.");
        return;
      }
      if (form.pathway === "individual") {
        if (!form.current_sor_is_egos) {
          toast.error("Please tell us whether Eletech Global Ontario School is your current Full-Time school.");
          return;
        }
        if (form.current_sor_is_egos === "no" && !form.full_time_school_name.trim()) {
          toast.error("Please enter the full name of your registered full-time school.");
          return;
        }
      }
      if (plarApplies(form) && !form.plar_completed) {
        toast.error("Please answer the PLAR question.");
        return;
      }
    }
    setStep((s) => Math.min(2, s + 1));
  };

  const toggleCourse = (code) =>
    setForm((f) => ({
      ...f,
      courses: f.courses.includes(code) ? f.courses.filter((c) => c !== code) : [...f.courses, code],
    }));

  const submit = async () => {
    if (!form.consent_privacy || !form.consent_refund) {
      toast.error("Please agree to the Privacy policy and the Refund Policy before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/applications", form);
      setRefId(res.data.ref_id);
      cartApi.clear();
      toast.success("Application received — welcome to Eletech!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const FeeLine = ({ f, dark }) => (
    <p className={`flex items-start justify-between gap-6 text-sm ${dark ? "text-ivory/80" : "text-slate-600"}`}>
      <span>{f.name}</span>
      <strong className={dark ? "text-ivory" : "text-ink"}>${f.amount}</strong>
    </p>
  );

  const FeeSummary = ({ dark = false }) => {
    const splitAt = dividerIdx > 0 ? dividerIdx : fees.length;
    const tuitionBlock = fees.slice(0, splitAt);
    const adminBlock = fees.slice(splitAt);
    return (
      <div className={`rounded-2xl border p-6 ${dark ? "border-ink/20 bg-ink text-ivory" : "border-brand/30 bg-brandlight/60"}`} data-testid={dark ? "application-fee-review" : "application-fee-summary"}>
        <p className={`font-mono text-xs uppercase tracking-[0.2em] ${dark ? "text-ivory/50" : "text-brand"}`}>Fee summary</p>
        <div className="mt-3 space-y-2">
          {tuitionBlock.map((f) => (
            <FeeLine key={f.name} f={f} dark={dark} />
          ))}
          {bundled > 0 && (
            <p className="flex items-start justify-between gap-6 text-sm text-aqua" data-testid="bundled-discount-line">
              <span>Scholarship support for Tuition bundle</span>
              <strong>-${bundled}</strong>
            </p>
          )}
          {bundled > 0 && couponInfo && couponInfo.discount_type === "percent" && (
            <p className={`flex items-start justify-between gap-6 text-sm ${dark ? "text-ivory/80" : "text-slate-600"}`} data-testid="tuition-total-line">
              <span>Tuition total</span>
              <strong className={dark ? "text-ivory" : "text-ink"}>${tuitionAmount - bundled}</strong>
            </p>
          )}
          {discount > 0 && couponInfo && (
            <p className="flex items-start justify-between gap-6 text-sm text-aqua" data-testid="partner-discount-line">
              <span>
                {couponInfo.discount_type === "percent" ? "Scholarship for application" : `Partner discount — ${couponInfo.code} ($${couponInfo.discount_value} off)`}
              </span>
              <strong>-${discount}</strong>
            </p>
          )}
          {tuitionBlock.length > 0 && adminBlock.length > 0 && (
            <div className="my-2.5 border-t border-slate-200" data-testid="fee-summary-divider" />
          )}
          {adminBlock.map((f) => (
            <FeeLine key={f.name} f={f} dark={dark} />
          ))}
          <p className={`flex items-center justify-between gap-6 border-t pt-3 ${dark ? "border-ivory/20" : "border-brand/30"}`}>
            <strong className={dark ? "text-sm text-ivory" : "text-sm text-ink"}>{bundled > 0 && discount > 0 ? "Grand Total" : "Total"}</strong>
            <strong className={`font-mono text-xl ${dark ? "text-brandlight" : "text-brand"}`} data-testid={dark ? "application-total-review" : "application-total"}>
              ${total}
            </strong>
          </p>
        </div>
      </div>
    );
  };

  if (refId) {
    return (
      <section className="container-x flex min-h-[70vh] items-center justify-center py-20" data-testid="application-success">
        <Reveal>
          <div className="card-line max-w-lg p-10 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-aqualight text-aqua">
              <Check className="h-7 w-7" />
            </span>
            <h2 className="mt-6 font-display text-2xl font-extrabold text-ink">You are in, {form.student_name.split(" ")[0]}.</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              Your application and fee summary (${total}) have been received. Save this reference — our admissions team will
              email {form.email} within two business days with your welcome package, invoice and course access.
            </p>
            <p className="mt-6 rounded-xl border border-brand/30 bg-brandlight px-6 py-4 font-mono text-2xl font-bold tracking-wider text-ink" data-testid="application-reference">
              {refId}
            </p>
            <p className="mt-6 text-left text-xs leading-relaxed text-slate-500" data-testid="application-documents-note">
              <strong className="font-semibold text-ink">Documents to come:</strong> The admissions team will reach out via your registered email to collect required documents, including previous marksheets/transcripts, prerequisite credit confirmation, community hours, a consent/records request form for your previous full-time school, a government-issued photo ID with your legal name and date of birth, proof of residency, and any other required materials. Please ensure these are completed to the admissions team's satisfaction within 7 days of receiving the initial request from EGOS.
            </p>
          </div>
        </Reveal>
      </section>
    );
  }

  return (
    <>
      <PageHero
        eyebrow="Admissions"
        lines={[<>Five minutes now,</>, <span className="font-serif italic font-medium text-brand">a head start for years</span>]}
        desc="No transcripts needed to start, no waiting for a semester. Complete this short application and a member of our team will personally set up your courses."
        testid="admissions-page-heading"
      />

      <section className="container-x py-20 sm:py-28" data-testid="application-form-section">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <div className="flex items-center gap-2" data-testid="application-progress">
              {STEP_TITLES.map((t, i) => (
                <div key={t} className="flex flex-1 items-center gap-2">
                  <span
                    className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full font-mono text-xs font-bold transition-colors ${
                      i <= step ? "bg-ink text-ivory" : "bg-slate-200 text-slate-500"
                    }`}
                  >
                    {i + 1}
                  </span>
                  <span className={`hidden text-xs font-semibold sm:block ${i <= step ? "text-ink" : "text-slate-400"}`}>{t}</span>
                  {i < STEP_TITLES.length - 1 && <span className={`h-px flex-1 ${i < step ? "bg-ink" : "bg-slate-200"}`} />}
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="card-line mt-8 p-6 sm:p-10" data-testid="application-form">
              {step === 0 && (
                <div className="grid gap-5 sm:grid-cols-2" data-testid="application-step-1">
                  <div className="sm:col-span-2">
                    <label className="field-label">Student full name *</label>
                    <input value={form.student_name} onChange={(e) => set("student_name", e.target.value)} className="field" placeholder="Jordan Meyer" data-testid="application-name-input" />
                  </div>
                  <div>
                    <label className="field-label">Student email *</label>
                    <input value={form.email} onChange={(e) => set("email", e.target.value)} className="field" placeholder="you@email.com" type="email" data-testid="application-email-input" />
                  </div>
                  <div>
                    <label className="field-label">Phone *</label>
                    <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="field" placeholder="+1 (555) 000-0000" data-testid="application-phone-input" />
                  </div>
                  <div>
                    <label className="field-label">Student type *</label>
                    <select value={form.student_type} onChange={(e) => setType(e.target.value)} className="field" data-testid="application-type-select">
                      <option value="ontario">Ontario based</option>
                      <option value="out_of_province">Out-of-province (Canada)</option>
                      <option value="international">International Students</option>
                    </select>
                  </div>
                  <div>
                    <label className="field-label">Current School Grade Level *</label>
                    <select value={form.grade_level} onChange={(e) => setGrade(e.target.value)} className="field" data-testid="application-grade-select">
                      <option value="">Choose a grade…</option>
                      <option value="9">Entering Grade 9</option>
                      <option value="10">Entering Grade 10</option>
                      <option value="11">Entering Grade 11</option>
                      <option value="12">Entering Grade 12</option>
                      <option value="homeschool">Homeschool</option>
                    </select>
                  </div>
                  <div>
                    <label className="field-label">Parent/Guardian name *</label>
                    <input value={form.parent_name} onChange={(e) => set("parent_name", e.target.value)} className="field" placeholder="Alex Meyer" data-testid="application-parent-name-input" />
                  </div>
                  <div>
                    <label className="field-label">Parent/Guardian email *</label>
                    <input value={form.parent_email} onChange={(e) => set("parent_email", e.target.value)} className="field" placeholder="parent@email.com" type="email" data-testid="application-parent-email-input" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="field-label">Address *</label>
                    <input value={form.address} onChange={(e) => set("address", e.target.value)} className="field" placeholder="354 Rymal Rd E" data-testid="application-address-input" />
                  </div>
                  <div>
                    <label className="field-label">School Board</label>
                    <input value={form.school_board} onChange={(e) => set("school_board", e.target.value)} className="field" placeholder="e.g. Hamilton-Wentworth DSB" data-testid="application-school-board-input" />
                  </div>
                  <div>
                    <label className="field-label">Country *</label>
                    {form.student_type === "international" ? (
                      <select value={form.country} onChange={(e) => set("country", e.target.value)} className="field" data-testid="application-country-select">
                        <option value="">Choose a country…</option>
                        {COUNTRIES.filter((c) => c !== "Canada").map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    ) : (
                      <select value="Canada" disabled className="field opacity-80" data-testid="application-country-select">
                        <option value="Canada">Canada</option>
                      </select>
                    )}
                  </div>
                  {form.student_type === "international" ? (
                    <div>
                      <label className="field-label">Province / State</label>
                      <input value={form.province} onChange={(e) => set("province", e.target.value)} className="field" placeholder="Your province or state" data-testid="application-province-input" />
                    </div>
                  ) : form.student_type === "out_of_province" ? (
                    <div>
                      <label className="field-label">Province *</label>
                      <select value={form.province} onChange={(e) => set("province", e.target.value)} className="field" data-testid="application-province-select">
                        <option value="">Choose a province…</option>
                        {CA_PROVINCES.filter((p) => p !== "Ontario").map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div>
                      <label className="field-label">Province *</label>
                      <select value="Ontario" disabled className="field opacity-80" data-testid="application-province-select">
                        <option value="Ontario">Ontario</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {step === 1 && (
                <div className="space-y-6" data-testid="application-step-2">
                  <div>
                    <p className="field-label">Choose your pathway *</p>
                    <div className="mt-2 grid gap-3 sm:grid-cols-2">
                      {[
                        { id: "individual", title: "Individual credits only", desc: "Pay per credit as you go. No OSR setup fee." },
                        { id: "ossd", title: "OSSD from EGOS", desc: "Work toward your diploma with EGOS — minimum required credits applicable. Full-Time School + OSR setup applies." },
                      ].map((o) => (
                        <label
                          key={o.id}
                          className={`cursor-pointer rounded-2xl border p-5 transition-all ${
                            form.pathway === o.id ? "border-brand bg-brandlight/60 shadow-sm" : "border-slate-200 hover:border-ink/40"
                          }`}
                        >
                          <input type="radio" name="pathway" checked={form.pathway === o.id} onChange={() => setPathway(o.id)} className="sr-only" data-testid={`application-pathway-${o.id}`} />
                          <span className="flex items-center justify-between">
                            <strong className="font-display text-base font-bold text-ink">{o.title}</strong>
                            {form.pathway === o.id && <Check className="h-4 w-4 text-brand" />}
                          </span>
                          <span className="mt-1.5 block text-xs leading-relaxed text-slate-500">{o.desc}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {form.pathway === "individual" && (
                    <div className="rounded-2xl border border-slate-200 bg-sand/50 p-5" data-testid="application-osr-block">
                      <p className="field-label">Is Eletech Global Ontario School your current Full-Time school? *</p>
                      <div className="mt-2 flex gap-3">
                        {[
                          { v: "yes", label: "Yes" },
                          { v: "no", label: "No" },
                        ].map((o) => (
                          <label
                            key={o.v}
                            className={`flex cursor-pointer items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
                              form.current_sor_is_egos === o.v ? "border-brand bg-brandlight/60 text-ink" : "border-slate-300 text-slate-500 hover:border-ink/40"
                            }`}
                          >
                            <input
                              type="radio"
                              name="sor"
                              checked={form.current_sor_is_egos === o.v}
                              onChange={() => setForm((f) => ({ ...f, current_sor_is_egos: o.v, full_time_school_name: o.v === "yes" ? "" : f.full_time_school_name }))}
                              className="sr-only"
                              data-testid={`application-sor-${o.v}`}
                            />
                            {o.label}
                          </label>
                        ))}
                      </div>
                      <p className="field-label mt-4">Your OEN (Ontario Education Number)</p>
                      <input
                        value={form.osr_info}
                        onChange={(e) => set("osr_info", e.target.value)}
                        className="field"
                        placeholder="Your 9 digit OEN number"
                        data-testid="application-osr-input"
                      />
                      {form.current_sor_is_egos === "no" && (
                        <div className="mt-4">
                          <p className="field-label">Full name of your registered full-time school *</p>
                          <input
                            value={form.full_time_school_name}
                            onChange={(e) => set("full_time_school_name", e.target.value)}
                            className="field"
                            placeholder="e.g. Northview Heights Secondary School"
                            data-testid="application-fulltime-school-input"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {plarApplies(form) && (
                    <div className="rounded-2xl border border-slate-200 bg-sand/50 p-5" data-testid="application-plar-block">
                      <p className="field-label">Have you already completed your Prior Learning Assessment and Recognition (PLAR) with EGOS? *</p>
                      <div className="mt-2 flex gap-3">
                        {[
                          { v: "yes", label: "Yes — completed" },
                          { v: "no", label: "No — I need the PLAR assessment" },
                        ].map((o) => (
                          <label
                            key={o.v}
                            className={`flex cursor-pointer items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
                              form.plar_completed === o.v ? "border-brand bg-brandlight/60 text-ink" : "border-slate-300 text-slate-500 hover:border-ink/40"
                            }`}
                          >
                            <input type="radio" name="plar" checked={form.plar_completed === o.v} onChange={() => set("plar_completed", o.v)} className="sr-only" data-testid={`application-plar-${o.v}`} />
                            {o.label}
                          </label>
                        ))}
                      </div>
                      <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-slate-600" data-testid="application-plar-note">
                        <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-brand" />
                        {PLAR_NOTE}
                      </p>
                    </div>
                  )}

                  <div>
                    <p className="field-label">
                      Select your course{form.courses.length > 1 ? "s" : ""} — {form.courses.length} chosen
                      {form.pathway === "ossd" && ` (minimum ${ossdMinCredits(form)} for the OSSD from EGOS pathway)`}
                    </p>
                    <p className="mt-1 text-xs text-slate-500" data-testid="application-prereq-note">Many courses have prerequisites, please ensure you've earned that credit.</p>
                    <div className="mt-2 grid max-h-96 gap-2 overflow-y-auto pr-2 sm:grid-cols-2">
                      {COURSES.map((c) => (
                        <label
                          key={c.code}
                          className={`flex cursor-pointer items-center gap-3 rounded-xl border p-4 transition-all ${
                            form.courses.includes(c.code) ? "border-brand bg-brandlight/60" : "border-slate-200 hover:border-ink/40"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={form.courses.includes(c.code)}
                            onChange={() => toggleCourse(c.code)}
                            data-testid={`application-course-check-${c.code}`}
                            className="h-4 w-4 accent-[#D97706]"
                          />
                          <span className="min-w-0">
                            <span className="flex items-center gap-2">
                              <span className="font-mono text-xs font-bold text-ink">{c.code}</span>
                              <span className="rounded-full bg-brandlight px-2 py-0.5 text-[10px] font-bold text-brand" data-testid={`application-course-grade-${c.code}`}>Grade {c.grade}</span>
                            </span>
                            <span className="block truncate text-sm text-slate-600">{c.name}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 p-5" data-testid="partner-code-block">
                    <p className="field-label">Partner Code (if applicable)</p>
                    {couponInfo ? (
                      <p className="flex items-center justify-between text-sm">
                        <span className="font-semibold text-aqua" data-testid="partner-code-applied">
                          ✓ {couponInfo.code} — {couponInfo.discount_type === "percent" ? `${couponInfo.discount_value}% off` : `$${couponInfo.discount_value} off`}
                        </span>
                        <button
                          type="button"
                          onClick={() => { setCouponInfo(null); setCouponInput(""); set("coupon_code", ""); }}
                          data-testid="partner-code-remove-btn"
                          className="text-xs font-bold text-slate-400 hover:text-ink"
                        >
                          Remove
                        </button>
                      </p>
                    ) : (
                      <div className="mt-2 flex gap-2">
                        <input
                          value={couponInput}
                          onChange={(e) => setCouponInput(e.target.value)}
                          className="field"
                          placeholder="Enter your partner code"
                          data-testid="partner-code-input"
                        />
                        <button type="button" onClick={applyCoupon} data-testid="partner-code-apply-btn" className="btn-ghost !px-6 whitespace-nowrap">
                          Apply
                        </button>
                      </div>
                    )}
                  </div>
                  <FeeSummary />
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6" data-testid="application-step-3">
                  <div>
                    <p className="field-label">Anything we should know?</p>
                    <textarea
                      value={form.message}
                      onChange={(e) => set("message", e.target.value)}
                      rows={4}
                      className="field"
                      placeholder="Goals, timelines, prerequisites you need for a university program…"
                      data-testid="application-message-input"
                    />
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-sand/60 p-6" data-testid="application-review">
                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Review</p>
                    <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                      <p><span className="text-slate-500">Student:</span> <strong className="text-ink">{form.student_name}</strong></p>
                      <p><span className="text-slate-500">Email:</span> <strong className="text-ink">{form.email}</strong></p>
                      <p><span className="text-slate-500">School grade level:</span> <strong className="text-ink">{form.grade_level === "homeschool" ? "Homeschool" : `Entering Grade ${form.grade_level}`}</strong></p>
                      <p><span className="text-slate-500">Type:</span> <strong className="text-ink">{TYPE_LABELS[form.student_type]}</strong></p>
                      <p><span className="text-slate-500">Parent/Guardian:</span> <strong className="text-ink">{form.parent_name} ({form.parent_email})</strong></p>
                      <p><span className="text-slate-500">Pathway:</span> <strong className="text-ink">{form.pathway === "ossd" ? "OSSD from EGOS" : "Individual credits only"}</strong></p>
                      {form.school_board && <p className="sm:col-span-2"><span className="text-slate-500">School board:</span> <strong className="text-ink">{form.school_board}</strong></p>}
                      <p className="sm:col-span-2"><span className="text-slate-500">Address:</span> <strong className="text-ink">{form.address}, {form.province && `${form.province}, `}{form.country}</strong></p>
                      {form.pathway === "individual" && (
                        <p className="sm:col-span-2"><span className="text-slate-500">OEN:</span> <strong className="text-ink">{form.osr_info || "—"}</strong> · <span className="text-slate-500">EGOS is your current Full-Time school:</span> <strong className="text-ink">{form.current_sor_is_egos === "yes" ? "Yes" : "No"}</strong>{form.current_sor_is_egos === "no" && form.full_time_school_name ? <> · <span className="text-slate-500">Registered full-time school:</span> <strong className="text-ink">{form.full_time_school_name}</strong></> : ""}</p>
                      )}
                      {form.extra_items.length > 0 && (
                        <p className="sm:col-span-2"><span className="text-slate-500">Services:</span> <strong className="text-ink">{form.extra_items.map((it) => (it.type === "plar" ? "PLAR assessment" : it.type === "extension" ? `Course extension (${it.months} month${it.months > 1 ? "s" : ""}${it.course_name ? ` — ${it.course_name}` : ""})` : `Course transfer (${it.from_course} → ${it.to_course})`)).join(", ")}</strong></p>
                      )}
                      {plarApplies(form) && (
                        <p className="sm:col-span-2"><span className="text-slate-500">PLAR with EGOS:</span> <strong className="text-ink">{form.plar_completed === "yes" ? "Already completed" : "Not completed — assessment fee applies"}</strong></p>
                      )}
                    </div>
                    <p className="mt-4 text-sm"><span className="text-slate-500">Courses:</span> <strong className="text-ink">{form.courses.join(", ")}</strong></p>
                    {couponInfo && <p className="mt-1 text-sm"><span className="text-slate-500">Partner code:</span> <strong className="text-ink">{couponInfo.code} — {couponInfo.discount_type === "percent" ? `${couponInfo.discount_value}% off` : `$${couponInfo.discount_value} off`}</strong></p>}
                  </div>
                  <FeeSummary dark />
                  <div className="rounded-2xl border border-slate-200 bg-sand/60 p-6" data-testid="application-consent-block">
                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Consent</p>
                    <p className="mt-2 text-sm font-semibold text-ink">Do you agree with the following?</p>
                    <label className="mt-3 flex cursor-pointer items-center gap-3 text-sm text-slate-600">
                      <input
                        type="checkbox"
                        checked={form.consent_privacy}
                        onChange={(e) => set("consent_privacy", e.target.checked)}
                        className="h-4 w-4 accent-brand"
                        data-testid="application-consent-privacy"
                      />
                      <span>
                        Yes — I agree with the{" "}
                        <a href="/privacy" target="_blank" rel="noreferrer" className="font-semibold text-brand underline underline-offset-2" data-testid="application-consent-privacy-link">
                          Privacy policy
                        </a>
                      </span>
                    </label>
                    <label className="mt-2 flex cursor-pointer items-center gap-3 text-sm text-slate-600">
                      <input
                        type="checkbox"
                        checked={form.consent_refund}
                        onChange={(e) => set("consent_refund", e.target.checked)}
                        className="h-4 w-4 accent-brand"
                        data-testid="application-consent-refund"
                      />
                      <span>
                        Yes — I agree with the{" "}
                        <a href="/refund-policy" target="_blank" rel="noreferrer" className="font-semibold text-brand underline underline-offset-2" data-testid="application-consent-refund-link">
                          Refund Policy
                        </a>
                      </span>
                    </label>
                  </div>
                </div>
              )}

              <div className="mt-10 flex items-center justify-between">
                {step > 0 ? (
                  <button onClick={() => setStep(step - 1)} data-testid="application-back-btn" className="btn-ghost !px-6 !py-2.5">
                    <ChevronLeft className="h-4 w-4 text-brand" /> Back
                  </button>
                ) : (
                  <span />
                )}
                {step < 2 ? (
                  <button onClick={next} data-testid="application-next-btn" className="btn-primary">
                    Continue <ChevronRight className="h-4 w-4 text-brand" />
                  </button>
                ) : (
                  <button onClick={submit} disabled={submitting} data-testid="application-submit-btn" className="btn-accent disabled:opacity-60">
                    {submitting ? "Submitting…" : <><Sparkles className="h-4 w-4" /> Submit <span className="text-ink">application</span></>}
                  </button>
                )}
              </div>
            </div>
          </Reveal>

          <Reveal className="mt-8">
            <div className="relative overflow-hidden rounded-3xl bg-ink p-8 text-ivory sm:p-10" data-testid="admissions-aside-panel">
              <DotGrid light className="right-6 top-6 h-32 w-32" />
              <img src="/logo.webp" alt="Eletech Global Ontario School crest" className="h-12 w-12 rounded-full bg-white" />
              <p className="mt-4 font-serif italic text-2xl text-brandlight">Five minutes of paperwork. A whole different school year.</p>
              <div className="mt-8 grid grid-cols-3 gap-6">
                <div>
                  <p className="font-mono text-2xl font-bold text-ivory">74</p>
                  <p className="mt-1 text-xs text-ivory/60">courses, always open</p>
                </div>
                <div>
                  <p className="font-mono text-2xl font-bold text-ivory">365</p>
                  <p className="mt-1 text-xs text-ivory/60">start days a year</p>
                </div>
                <div>
                  <p className="font-mono text-2xl font-bold text-ivory">110</p>
                  <p className="mt-1 text-xs text-ivory/60">guided hours per credit</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
